<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { useToast } from '../../composables/useToast';
import ProductsRepository from '@repositories/products.repository';
import { ISubAsset } from '../../types/subAssets.type';
import SubAssetsRepository from '@repositories/subAssets.repository';
import { IProduct } from '../../types/products.type';
import { useI18n } from "vue-i18n";
import DataTable from "@components/DataTable.vue";

const isLoadingProducts = ref<boolean>(false);


const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const { products: productsPinia }: any = storeToRefs(store);
const products = ref<IProduct[]>([]);
const subAssets = ref<ISubAsset[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedProducts = ref<IProduct | null>(null);
const isViewActive = ref<boolean>(true);
const fieldsTableInactive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'description',
    label: 'Sub-subAssets',
  },
  {
    key: 'restore',
    label: 'Restaurar',
  },
]);
const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'description',
    label: 'Sub-subAssets',
  },
  {
    key: 'riskType',
    label: 'Riesgo',
    formatter: (e: any) => e?.description || null,
  },
  {
    key: 'subAsset',
    label: 'Sub-Asset',
    formatter: (e: any) => e?.description || null,
  },
  {
    key: 'indemnificationLimitMin',
    label: t('global.limite_minimo'),
    formatter: (e: any) => e ?? '-',
  },
  {
    key: 'indemnificationLimitMax',
    label: t('global.limite_maximo'),
    formatter: (e: any) => e ?? '-',
  },
  {
    key: 'productInfo',
    label: 'Editar',
  },
  {
    key: 'delete',
    label: 'Eliminar',
  },
]);
const fieldsFilter: any = ref([
  {
    key: 'search',
    title: 'Buscar',
    type: 'text',
    placeholder: 'Buscar por descripcion del producto',
    value: null,
    cols: 3,
  },
  {
    key: 'subAssetId',
    title: 'Nombre del Sub-Asset',
    type: 'select',
    label: 'description',
    placeholder: 'Buscar por sub-asset',
    formatValue: (val: any) => val?.id ?? null,
    value: null,
    options: subAssets,
    cols: 3,
  },
  {
    key: 'riskType',
    title: 'Tipo de riesgo',
    label: 'description',
    type: 'select',
    placeholder: 'Buscar por tipo de riesgo',
    value: null,
    formatValue: (val: any) => val?.description ?? null,
    options: [
      { id: 1, description: 'LOW' },
      { id: 2, description: 'MEDIUM' },
      { id: 3, description: 'HIGH' },
    ],
    cols: 3,
  },
]);
const searchFormRef = ref<any>(null);

// API
const getProductsFromAPI = async (
  page: number,
  deleted: boolean = false,
  params = {},
) => {
  isLoadingProducts.value = true;
  try {
    const { data } = await ProductsRepository.getProducts({
      page,
      deleted,
      ...params,
    });

    if (data && Array.isArray(data?.products)) {
      paginationData.value = data.pagination;
      updateProducts(data.products);
      return data.products;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching products:', error);
    return [];
  } finally {
    isLoadingProducts.value = false;
  }
};

const getsubAssetsFromApi = async () => {
  const data = await SubAssetsRepository.getSubAssetsPublic();
  subAssets.value = data;
};

const updateProducts = (newProducts: IProduct[]) => {
  products.value = newProducts;
  store.setProp({ key: 'products', value: newProducts });
};

const loadProducts = async () => {
  await getProductsFromAPI(1);
};

onMounted(() => {
  loadProducts();
  getsubAssetsFromApi();
});

// Create inputs from modals
const fieldsInputProducts = computed(() => {
  return [
    {
      key: 'description',
      title: 'Nombre del producto',
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: 'Nuevo producto',
      value: selectedProducts.value?.description,
      cols: 12,
    },
    {
      key: 'indemnificationLimitMin',
      value: selectedProducts.value?.indemnificationLimitMin,
      title: t('global.limite_minimo'),
      type: 'text',
      placeholder: t('global.limite_minimo'),
      cols: 12,
      formatValue: (val: any) => val === '' ? null : val,
    },
    {
      key: 'indemnificationLimitMax',
      value: selectedProducts.value?.indemnificationLimitMax,
      title: t('global.limite_maximo'),
      type: 'text',
      placeholder: t('global.limite_maximo'),
      cols: 12,
      formatValue: (val: any) => val === '' ? null : val,
    },
    {
      key: 'subAssetId',
      label: 'description',
      selectLabel: null,
      value: selectedProducts.value?.subAsset.id,
      searchable: true,
      title: 'Sub-Asset',
      trackBy: 'id',
      type: 'select',
      placeholder: 'Nombre del asset',
      formatValue: (val: any) => val.id,
      cols: 12,
      options: subAssets,
    },
    {
      key: 'riskTypeId',
      label: 'description',
      value: selectedProducts.value?.riskType.id,
      searchable: true,
      title: 'Riesgo',
      type: 'select',
      trackBy: 'id',
      placeholder: 'Tipo de riesgo',
      formatValue: (val: any) => val.id,
      cols: 12,
      options: [
        { id: 1, description: 'LOW' },
        { id: 2, description: 'MEDIUM' },
        { id: 3, description: 'HIGH' },
      ],
    },
  ];
});

// UI
const onChangePage = async (page: number) => {
  getProductsFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = 'Agregar producto';
  isModalOpen.value = true;
  modalType.value = 'modalCreateProducts';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedProducts.value = null;
};

const handleActive = async () => {
  const products = await getProductsFromAPI(1);
  updateProducts(products);
  isViewActive.value = true;
};

const handleInactive = async () => {
  const productsDeleted = await getProductsFromAPI(1, true);
  updateProducts(productsDeleted);
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: IProduct) => {
  modalTitle.value = isViewActive.value
    ? `Borrar el producto con id: ${item.id}`
    : `Restaurar el producto con id: ${item.id}`;
  modalType.value = 'modalDialog';
  selectedProducts.value = item;
  isModalOpen.value = true;
};

// Filter Functions

const handleOnSubmit = async ({ items }: any) => {
  getProductsFromAPI(1, false, items);
};

const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  getProductsFromAPI(1, false);
};

//CRUD
const handleCreateProducts = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  try {
    const data = await ProductsRepository.createProduct(items);
    store.setProp({ key: 'products', value: [...products.value, data] });
	products.value = [...products.value, data];
    $toast?.success(`Producto agregado con exito`);
  } catch (error) {
    $toast?.error('Ocurrió un error al intentar crear');
  }

  handleClose();
};

const handleSelectProducts = (ProductsData: IProduct) => {
  modalTitle.value = 'Actualizar Producto';
  (isModalOpen.value = true), (modalType.value = 'modalUpdateProducts');
  selectedProducts.value = ProductsData;
};

const handleUpdateProducts = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedProducts.value) return;
  const assetForUpdate = {
    ...items,
    id: selectedProducts.value.id,
  };

  try {
    const data = await ProductsRepository.updateProduct(assetForUpdate);
    let index = store.products.findIndex((value: any) => value.id === data.id);
    if (index > -1) {
      store.setProp({ key: 'products', index, value: data });
    }
    $toast?.success(`Producto actualizado con exito`);
  } catch (error) {
    $toast?.error('Ocurrió un error al intentar actualizar');
  }

  handleClose();
};

const handleDeleteRestoreProducts = async () => {
  if (selectedProducts?.value?.id) {
    let response = null;
    try {
      if (isViewActive.value) {
        response = await ProductsRepository.deleteProduct(
          selectedProducts.value.id,
        );
      } else {
        response = await ProductsRepository.restoreProduct(
          selectedProducts.value.id,
        );
      }

      if (response === 200) {
        store.deleteItem({
          key: 'products',
          value: selectedProducts?.value?.id,
        });
      }
    } catch (error) {
      if (isViewActive.value) {
        return $toast?.error('Error al intentar eliminar');
      }
      $toast?.error('Error al intentar restaurar');
    }

    handleClose();
  }
};
</script>

<template>
  <Layout title="Productos" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>Activos</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>Inactivos</template>
          </CButton>
        </div>
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content> Agregar producto </template>
        </CButton>
      </div>

      <div class="form__logEmail">
        <Form
          ref="searchFormRef"
          @onSubmit="handleOnSubmit"
          :inputs="fieldsFilter"
          customClass="poliza__form"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
            >
              <template #content> Buscar </template>
            </CButton>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="handleReset"
            >
              <template #content> Limpiar filtro </template>
            </CButton>
          </template>
        </Form>
      </div>

      <DataTable
        :loading="isLoadingProducts"
        :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="productsPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(productInfo)="assetData">
          <div
            @click="handleSelectProducts(assetData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon restore"
          >
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateProducts'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputProducts"
            @onSubmit="handleCreateProducts"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>Agregar producto</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateProducts'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputProducts"
            @onSubmit="handleUpdateProducts"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>Actualizar</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `¿Está seguro que desea borrar el producto ${selectedProducts?.description}?`
                : `¿Está seguro que desea restaurar el producto ${selectedProducts?.description}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDeleteRestoreProducts"
            >
              <template #content>
                <div>
                  <span>Confirmar</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
